import _get from 'lodash/get';
import _pick from 'lodash/pick';

import { extractErrorMessage } from 'ravenjs/utils/error';

import { MULTI_FACTOR_AUTHENTICATION_FEATURE_ENABLED } from 'constants/api';
import {
    MFA_ATTEMPTS_REMAINING,
    MFA_ERROR_TYPE_BY_CODE,
    MFA_ERROR_TYPES,
    MFA_INVALID_CODE_ERROR,
    MFA_LOGIN_CONTEXT_KEYS,
    MFA_MAX_ATTEMPTS_ERROR,
    MFA_REQUIRED_STATUSES,
    MFA_ROUTE_BY_STATUS,
    MFA_SESSION_EXPIRED_ERROR,
    MFA_TERMINAL_ERROR_TYPES,
} from 'constants/mfa';
import { convertStringToBoolean } from 'utils/common';

/**
 * Determine whether the MFA feature is enabled.
 * This lives here, and not in `utils/company`, so the auth sagas can read the
 * flag without pulling the store into a circular dependency.
 *
 * @method isMFAEnabled
 * @return {boolean}
 */
export const isMFAEnabled = () =>
    convertStringToBoolean(MULTI_FACTOR_AUTHENTICATION_FEATURE_ENABLED);

/**
 * Build the MFA requirement out of a `POST /v2/oauth/authenticate` response.
 * Returns `null` when the response is a regular (non MFA) token response.
 *
 * @method getMFARequirement
 * @param  {Object}          [response={}] The authenticate response body
 * @return {Object|null}                   The MFA requirement, or null
 */
export const getMFARequirement = (response = {}) => {
    const status = _get(response, 'status', '');
    const mfaToken = _get(response, 'mfa_token', '');

    if (!mfaToken || !MFA_REQUIRED_STATUSES.includes(status)) {
        return null;
    }

    return {
        allowedMethods: _get(response, 'allowed_methods', []),
        message: _get(response, 'message', ''),
        method: _get(response, 'mfa_method', ''),
        mfaToken,
        status,
    };
};

/**
 * Determine whether the given authenticate response requires the user
 * to complete an MFA flow before signing in.
 *
 * @method isMFARequiredResponse
 * @param  {Object}              [response={}] The authenticate response body
 * @return {boolean}
 */
export const isMFARequiredResponse = (response = {}) => Boolean(getMFARequirement(response));

/**
 * Get the MFA route for the given authenticate `status`, i.e. the verification
 * screen for an enrolled user and the setup screen for a user without a method.
 *
 * @method getMFARouteByStatus
 * @param  {string}            [status=''] The authenticate `status`
 * @return {string}                        The MFA route
 */
export const getMFARouteByStatus = (status = '') => _get(MFA_ROUTE_BY_STATUS, status, '');

/**
 * Pick the login credentials that are safe to keep around while the user
 * completes the MFA flow, so the post sign in redirect stays intact.
 *
 * @method getMFALoginContext
 * @param  {Object}           [userCreds={}] The login credentials
 * @return {Object}                          The login context
 */
export const getMFALoginContext = (userCreds = {}) => _pick(userCreds, MFA_LOGIN_CONTEXT_KEYS);

/**
 * Build the counter of the attempts left in the current MFA challenge.
 *
 * @method getMFAAttemptsRemainingMessage
 * @param  {number}                       remainingAttempts The attempts left
 * @return {string}                                         The counter to render
 */
export const getMFAAttemptsRemainingMessage = remainingAttempts => {
    const template =
        remainingAttempts === 1 ? MFA_ATTEMPTS_REMAINING.SINGULAR : MFA_ATTEMPTS_REMAINING.PLURAL;

    return template.replace('ATTEMPTS_LEFT', `${remainingAttempts}`);
};

/**
 * Build the error state the verify identity screen renders for a failed
 * verification out of the `errorCode` of the response.
 * A rejected code (`1062`) is retryable and carries the `remainingAttempts` of
 * the challenge, an exhausted challenge or an unusable MFA session is terminal
 * and sends the user back to the login screen. Every other code is rendered
 * with the message of the API itself and keeps the screen retryable.
 *
 * @method getMFAErrorState
 * @param  {Error|Object}   [error] The error of the last verification attempt
 * @return {Object}                 The error state of the screen
 */
export const getMFAErrorState = error => {
    if (!error) {
        return { errorType: '', isTerminal: false, message: '', remainingAttempts: null };
    }

    const data = _get(error, 'response.data', {});
    const errorCode = `${_get(data, 'errorCode', '')}`;
    const errorType = _get(MFA_ERROR_TYPE_BY_CODE, errorCode, '');
    const remainingAttempts = _get(data, 'remainingAttempts', null);
    const isTerminal = MFA_TERMINAL_ERROR_TYPES.includes(errorType);

    let message = extractErrorMessage(error) || MFA_INVALID_CODE_ERROR;

    if (errorType === MFA_ERROR_TYPES.INVALID_CODE) {
        message = MFA_INVALID_CODE_ERROR;
    } else if (errorType === MFA_ERROR_TYPES.MAX_ATTEMPTS) {
        message = MFA_MAX_ATTEMPTS_ERROR;
    } else if (errorType === MFA_ERROR_TYPES.SESSION_EXPIRED) {
        message = MFA_SESSION_EXPIRED_ERROR;
    }

    return { errorType, isTerminal, message, remainingAttempts };
};
